<template>
  <div>
    <multiline-input v-once
                     ref="subject"
                     class="subject"
                     :value="subject"
                     :placeholder="__('Title')"
                     dir="auto"
                     :newline="false"
                     @input="onSubjectChange"
                     @enter="onSubjectEnterKeydown"
                     @focus="onSubjectFocus" />
    <!-- v-once to render the RTE only once so that the caret won't jump when the wall is remotedly updated -->
    <rich-text-editor v-once
                      ref="editor"
                      class="body"
                      :toolbar-id="toolbarId"
                      :value="body"
                      :placeholder="__('Write something ...')"
                      dir="auto"
                      @input="onBodyChange"
                      @focus="onBodyFocus"
                      @blur="onBodyBlur"
                      @selectionchange="onBodySelectionChange"
                      @fileaccept="onBodyFileAccept" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import MultilineInput from 'vuecomponents/multiline_input'
import RichTextEditor from 'vuecomponents/rich_text_editor'
import IntlMixin from 'vuemixins/intl_mixin'
import TrixToolbarMixin from 'vuemixins/trix_toolbar_mixin'
import { $ } from 'bits/jquery'
import uniqueId from 'lodash.uniqueid'

export default {
  components: { MultilineInput, RichTextEditor },
  mixins: [IntlMixin, TrixToolbarMixin],
  props: {
    post: { type: Object, required: true },
  },
  data () {
    return {
      toolbarComponent: null,
      editorComponent: null,
      toolbarInputFocused: false,
    }
  },
  computed: {
    ...mapState(['isMobile', 'isApp']),
    ...mapState('map', ['map']),
    toolbarId () {
      return uniqueId('trixtoolbar')
    },
    subject () {
      return this.post.subject
    },
    body () {
      return this.post.body
    },
    floatingToolbar () { return !this.isMobile },
    fixedToolbar () { return this.isMobile && !this.isApp },
  },
  watch: {
    toolbarInputFocused (newValue) {
      if (newValue && this.fixedToolbar) {
        this.showTrixToolbar()
      }
    },
  },
  async mounted () {
    this.$nextTick(() => this.focus())
    if (this.map) {
      // words_edit on Map need refocusing after 100ms in order
      // to regain focus on the words_edit after the map is panned or zoomed
      // once the popup is opened
      const { MapEventsManager } = await import('bits/map')
      this.endBoundsChangedListener = MapEventsManager.addListener(this.map, 'idle', () => {
        setTimeout(() => {
          this.focus()
        }, 100)
      })
    }
  },
  updated () {
    const minHeight = $('.body', this.$el).css('lineHeight')
    $(this.$refs.editor.$refs.editor).css('min-height', minHeight)
  },
  beforeDestroy () {
    this.hideTrixToolbar()
    this.endBoundsChangedListener && this.endBoundsChangedListener.remove()
  },
  methods: {
    focus () {
      if (!this.post.id) { // post is new
        this.$refs.subject.focus()
      } else {
        this.$refs.editor.focus()
      }
    },
    onSubjectChange (newValue) {
      this.$emit('text-change', {
        subject: newValue,
      })
    },

    onSubjectFocus (e) {
      if (!this.floatingToolbar) {
        this.hideTrixToolbar()
      }
    },

    onSubjectEnterKeydown (e) {
      $(e.currentTarget).blur()
      this.$nextTick(() => {
        $(this.$refs.editor).focus()
      })
    },

    onBodyChange (html) {
      const sanitizedHtml = html.replace(/<span style="background-color: highlight;">(.*?)<\/span>/g, '$1')
      this.$emit('text-change', {
        body: sanitizedHtml,
      })
    },
    onBodyFocus () {
      if (this.fixedToolbar) {
        this.showTrixToolbar()
      }
    },
    onBodyBlur () {
      if (this.fixedToolbar && !this.toolbarInputFocused) {
        this.hideTrixToolbar()
      }
    },
    onBodySelectionChange () {
      this.repositionTrixToolbar()
    },
    onBodyFileAccept (e) {
      e.preventDefault()
    },
  },
}
</script>

<style lang="scss" scoped>
.multiline-input {
    overflow: hidden;
    box-shadow: none;
    resize: none;
    border: none;
    transition: none;
    margin: 0;
    padding: 0;
}
</style>
